import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  Check,
  Clock,
  Loader2,
  MapPin,
  Video,
} from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Calendar } from "@/components/ui/calendar";
import { api, formatApiErrorDetail, trackEvent } from "@/lib/api";

const STEPS = ["Location", "Service", "Date & Time", "Your details", "Confirm"];

const cardCls =
  "bg-cream-soft border border-sand/70 rounded-[2rem] p-6 md:p-8";
const inputCls =
  "w-full bg-cream-soft border border-sand/80 rounded-2xl px-5 py-3.5 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy transition-colors";

export default function Book() {
  const [params] = useSearchParams();
  const prefillService = params.get("service");
  const prefillLocation = params.get("location");

  const [step, setStep] = useState(0);
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [locationSlug, setLocationSlug] = useState("");
  const [serviceSlug, setServiceSlug] = useState("");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    referral_source: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    trackEvent("page_view", "/book");
    trackEvent("booking_started", "/book");
  }, []);

  useEffect(() => {
    if (prefillLocation) setLocationSlug(prefillLocation);
    if (prefillService) setServiceSlug(prefillService);
    if (prefillLocation && prefillService) setStep(2);
    else if (prefillLocation) setStep(1);
    else if (prefillService) setStep(0);
  }, [prefillLocation, prefillService]);

  const selectedLocation = useMemo(
    () => locations.find((l) => l.slug === locationSlug),
    [locations, locationSlug]
  );
  const selectedService = useMemo(
    () => services.find((s) => s.slug === serviceSlug),
    [services, serviceSlug]
  );

  useEffect(() => {
    if (!locationSlug || !date) return;
    setSlotsLoading(true);
    setStartTime("");
    const dateStr = format(date, "yyyy-MM-dd");
    api
      .get("/availability", { params: { location_slug: locationSlug, date: dateStr } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [locationSlug, date]);

  // Disable dates where location not open
  const disabledMatcher = (d) => {
    if (!selectedLocation) return true;
    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const weekday = String((d.getDay() + 6) % 7); // convert Sun=0 -> Mon=0
    return !(selectedLocation.schedule && weekday in selectedLocation.schedule);
  };

  const canNext = [
    !!locationSlug,
    !!serviceSlug,
    !!date && !!startTime,
    form.client_name.length >= 2 &&
      /^\S+@\S+\.\S+$/.test(form.client_email) &&
      form.client_phone.length >= 6,
    true,
  ][step];

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        location_slug: locationSlug,
        service_slug: serviceSlug,
        date: format(date, "yyyy-MM-dd"),
        start_time: startTime,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        referral_source: form.referral_source,
        notes: form.notes,
      };
      const { data } = await api.post("/bookings", payload);
      trackEvent("booking_completed", "/book");
      setConfirmation(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Unable to book");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) return <Confirmation booking={confirmation} />;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.22em] text-navy mb-3">Book a session</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-ink leading-tight">
            Let's find a time that feels right.
          </h1>
        </div>

        <Stepper step={step} />

        <div className="mt-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              {step === 0 && (
                <div className={cardCls}>
                  <div className="font-serif text-2xl text-ink mb-6">Where would you like to meet?</div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {locations.map((loc) => (
                      <button
                        key={loc.slug}
                        data-testid={`pick-location-${loc.slug}`}
                        onClick={() => setLocationSlug(loc.slug)}
                        className={`text-left rounded-3xl p-6 border-2 transition-all duration-300 ${
                          locationSlug === loc.slug
                            ? "border-navy bg-cream shadow-[0_14px_40px_-18px_rgba(14,59,74,0.55)]"
                            : "border-sand/70 bg-cream/50 hover:border-navy/60"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-navy mb-4">
                          {loc.type === "online" ? <Video size={14} /> : <MapPin size={14} />}
                          {loc.type === "online" ? "Telehealth" : "In person"}
                        </div>
                        <div className="font-serif text-xl text-ink mb-2 leading-tight">{loc.name}</div>
                        <div className="text-sm text-ink-muted leading-relaxed font-light line-clamp-3">
                          {loc.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className={cardCls}>
                  <div className="font-serif text-2xl text-ink mb-6">Which session is for you?</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {services.map((s) => (
                      <button
                        key={s.slug}
                        data-testid={`pick-service-${s.slug}`}
                        onClick={() => setServiceSlug(s.slug)}
                        className={`text-left rounded-3xl p-7 border-2 transition-all duration-300 ${
                          serviceSlug === s.slug
                            ? "border-navy bg-cream shadow-[0_14px_40px_-18px_rgba(14,59,74,0.55)]"
                            : "border-sand/70 bg-cream/50 hover:border-navy/60"
                        }`}
                      >
                        <div className="flex items-baseline justify-between mb-3">
                          <span className="text-xs uppercase tracking-[0.2em] text-ink-soft">{s.audience}</span>
                        </div>
                        <div className="font-serif text-2xl text-ink mb-2 leading-tight">{s.name}</div>
                        <div className="text-sm text-ink-muted font-light leading-relaxed">{s.description}</div>
                        <div className="mt-4 text-xs text-ink-soft uppercase tracking-[0.18em]">
                          {s.duration_min} min • online or in-person
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className={cardCls}>
                  <div className="font-serif text-2xl text-ink mb-6">Pick a day & time</div>
                  <div className="grid md:grid-cols-2 gap-8 items-start">
                    <div className="bg-cream rounded-3xl p-2 md:p-4 border border-sand/70">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={disabledMatcher}
                        data-testid="booking-calendar"
                        className="!p-0"
                      />
                      <div className="mt-2 px-3 pb-3 text-xs text-ink-soft">
                        {selectedLocation?.type === "online"
                          ? "Online: Mon–Fri, 9am–7pm"
                          : selectedLocation?.slug === "midway-point"
                          ? "Midway Point: Mon–Wed, 9am–5pm"
                          : selectedLocation?.slug === "risdonvale"
                          ? "Risdonvale: Thu–Fri, 9am–5pm"
                          : ""}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm uppercase tracking-[0.18em] text-ink-soft mb-4">
                        Available times {date ? `— ${format(date, "EEE d MMM")}` : ""}
                      </div>
                      {!date && (
                        <div className="text-ink-muted text-sm italic">Choose a date first.</div>
                      )}
                      {date && slotsLoading && (
                        <div className="flex items-center gap-2 text-ink-muted text-sm">
                          <Loader2 size={16} className="animate-spin" /> Finding available slots…
                        </div>
                      )}
                      {date && !slotsLoading && slots.length === 0 && (
                        <div className="text-ink-muted text-sm italic">
                          No slots open on this day. Try another date.
                        </div>
                      )}
                      {date && !slotsLoading && slots.length > 0 && (
                        <div className="grid grid-cols-3 gap-3">
                          {slots.map((s) => (
                            <button
                              key={s}
                              onClick={() => setStartTime(s)}
                              data-testid={`slot-${s}`}
                              className={`rounded-xl py-3 text-sm font-medium border transition-all duration-200 ${
                                startTime === s
                                  ? "bg-navy text-cream border-navy"
                                  : "bg-cream text-ink border-sand/80 hover:border-navy hover:text-navy"
                              }`}
                            >
                              {formatTime(s)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className={cardCls}>
                  <div className="font-serif text-2xl text-ink mb-6">A few quick details</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      data-testid="booking-name-input"
                      placeholder="Full name *"
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      className={inputCls}
                    />
                    <input
                      data-testid="booking-email-input"
                      type="email"
                      placeholder="Email address *"
                      value={form.client_email}
                      onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                      className={inputCls}
                    />
                    <input
                      data-testid="booking-phone-input"
                      placeholder="Phone number *"
                      value={form.client_phone}
                      onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                      className={`${inputCls} md:col-span-2`}
                    />
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-[0.18em] text-ink-soft mb-2">
                        How did you hear about us?
                      </label>
                      <select
                        data-testid="booking-referral-select"
                        value={form.referral_source}
                        onChange={(e) => setForm({ ...form, referral_source: e.target.value })}
                        className={`${inputCls} appearance-none bg-white cursor-pointer`}
                      >
                        <option value="">Select an option (optional)</option>
                        <option value="Google search">Google search</option>
                        <option value="GP / health professional referral">GP / health professional referral</option>
                        <option value="Friend or family">Friend or family</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Neighbourhood House">Neighbourhood House</option>
                        <option value="Returning client">Returning client</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <textarea
                      data-testid="booking-notes-input"
                      placeholder="Anything you'd like Jesse to know before the session? (optional)"
                      rows={4}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className={`${inputCls} md:col-span-2 resize-none`}
                    />
                  </div>
                  <p className="text-xs text-ink-soft mt-4">
                    Your information is stored confidentially and never shared.
                  </p>
                </div>
              )}

              {step === 4 && (
                <div className={cardCls}>
                  <div className="font-serif text-2xl text-ink mb-6">One last look</div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Row label="Service" value={selectedService?.name} sub={`${selectedService?.duration_min} min`} />
                    <Row label="Location" value={selectedLocation?.name} sub={selectedLocation?.address} />
                    <Row label="Date" value={date ? format(date, "EEEE, d MMMM yyyy") : ""} />
                    <Row label="Time" value={formatTime(startTime)} />
                    <Row label="Name" value={form.client_name} />
                    <Row label="Contact" value={`${form.client_email} · ${form.client_phone}`} />
                    {form.referral_source && (
                      <Row label="How you heard about us" value={form.referral_source} />
                    )}
                  </div>
                  {form.notes && (
                    <div className="mt-6 bg-cream rounded-2xl p-5 border border-sand/70">
                      <div className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2">Your notes</div>
                      <div className="text-ink leading-relaxed text-sm">{form.notes}</div>
                    </div>
                  )}

                  <p className="mt-8 text-xs text-ink-soft leading-relaxed" data-testid="intake-followup-note">
                    After you confirm, we'll email you a short intake form to complete before your session. Our payment terms and late-fees policy sit inside that form for you to review and sign at your own pace.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav */}
        <div className="flex justify-between mt-10">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="booking-back-button"
            className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink px-6 py-3 text-sm font-medium hover:border-navy hover:text-navy disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ArrowLeft size={14} /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              data-testid="booking-next-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-7 py-3 text-sm font-medium hover:bg-navy-deep hover:-translate-y-0.5 shadow-[0_12px_30px_-14px_rgba(14,59,74,0.75)] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="booking-confirm-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy-deep text-cream px-8 py-3 text-sm font-medium hover:bg-navy hover:-translate-y-0.5 shadow-[0_12px_34px_-12px_rgba(7,42,55,0.8)] transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
              Confirm booking
            </button>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
      {STEPS.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={label} className="flex items-center gap-2 sm:gap-3">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                done
                  ? "bg-navy-light text-cream"
                  : active
                  ? "bg-navy text-cream"
                  : "bg-cream-soft border border-sand/70 text-ink-soft"
              }`}
            >
              {done ? <Check size={14} /> : i + 1}
            </div>
            <div
              className={`text-xs sm:text-sm ${
                active ? "text-ink font-medium" : "text-ink-soft"
              }`}
            >
              {label}
            </div>
            {i < STEPS.length - 1 && (
              <div className="hidden sm:block h-px w-6 bg-sand/80" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className="bg-cream rounded-2xl p-5 border border-sand/70">
      <div className="text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-1">{label}</div>
      <div className="font-serif text-lg text-ink leading-tight">{value || "—"}</div>
      {sub && <div className="text-xs text-ink-muted mt-1">{sub}</div>}
    </div>
  );
}

function formatTime(hhmm) {
  if (!hhmm) return "—";
  const [h] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "pm" : "am";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:00 ${suffix}`;
}

function Confirmation({ booking }) {
  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="booking-confirmation"
          className="bg-cream-soft border border-sand/70 rounded-[2rem] p-10 text-center"
        >
          <div className="mx-auto h-16 w-16 rounded-full bg-navy text-cream flex items-center justify-center mb-6">
            <Check size={28} />
          </div>
          <div className="text-xs uppercase tracking-[0.22em] text-navy mb-3">Booking confirmed</div>
          <h1 className="font-serif text-4xl text-ink mb-3">You're in, {booking.client_name.split(" ")[0]}.</h1>
          <p className="text-ink-muted max-w-lg mx-auto leading-relaxed font-light mb-10">
            A confirmation has been saved under reference <span className="font-medium text-ink">{booking.id.slice(0, 8).toUpperCase()}</span>.
            Jesse will be in touch on {booking.client_email} with any final details.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 text-left">
            <Row label="Service" value={booking.service_name} />
            <Row label="Location" value={booking.location_name} />
            <Row label="Date" value={format(new Date(booking.date), "EEEE, d MMMM yyyy")} />
            <Row label="Time" value={`${formatTime(booking.start_time)} – ${formatTime(booking.end_time)}`} />
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/"
              data-testid="back-home-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 text-ink px-6 py-3 text-sm font-medium hover:border-navy hover:text-navy transition-all"
            >
              <ArrowLeft size={14} /> Back home
            </Link>
            <a
              href="/book"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-3 text-sm font-medium hover:bg-navy-deep transition-all"
            >
              <Clock size={14} /> Book another
            </a>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
